import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import API_URL from '../config/api.js';
import axios from 'axios';

function AdminContactsManagement() {
  const [calendars, setCalendars] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [editingContact, setEditingContact] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [calendarsRes, contactsRes] = await Promise.all([
        axios.get(`${API_URL}/api/availability/calendars`),
        axios.get(`${API_URL}/api/referrals/admin/contacts`)
      ]);
      
      setCalendars(calendarsRes.data.calendars || []);
      setContacts(contactsRes.data.contacts || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveContact = async () => {
    if (!editingContact.phoneNumber || !editingContact.name) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      await axios.post(`${API_URL}/api/referrals/admin/contacts`, editingContact);
      await loadData();
      setEditingContact(null);
    } catch (error) {
      console.error('Error saving contact:', error);
      alert('Failed to save contact');
    }
  };

  const deleteContact = async (calendarId) => {
    if (!confirm('Are you sure you want to delete this contact?')) return;

    try {
      await axios.delete(`${API_URL}/api/referrals/admin/contacts/${calendarId}`);
      await loadData();
    } catch (error) {
      console.error('Error deleting contact:', error);
    }
  };

  const editContact = (contact) => {
    setEditingContact({
      calendarId: contact.calendarId._id || contact.calendarId,
      phoneNumber: contact.phoneNumber,
      name: contact.name,
      email: contact.email || ''
    });
  };

  const addNewContact = () => {
    setEditingContact({
      calendarId: '',
      phoneNumber: '',
      name: '',
      email: ''
    });
  };

  if (loading) {
    return (
      <div className="loading-state">
        <div className="spinner"></div>
        <p>Loading contacts...</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="contacts-section"
    >
      <div className="section-header">
        <div>
          <h2>Admin Contacts</h2>
          <p style={{ color: '#666', fontSize: '14px', marginTop: '8px' }}>
            Set who receives SMS notifications when appointments are booked for each calendar
          </p>
        </div>
        <motion.button
          className="create-button"
          onClick={addNewContact}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          + Add Contact
        </motion.button>
      </div>

      {contacts.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📞</div>
          <h3>No admin contacts set</h3>
          <p>Add contacts to receive SMS notifications for appointment bookings</p>
        </div>
      ) : (
        <div className="contacts-grid" style={{ display: 'grid', gap: '16px', marginTop: '24px' }}>
          {contacts.map((contact) => (
            <motion.div
              key={contact._id}
              className="contact-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                background: 'white',
                padding: '20px',
                borderRadius: '12px',
                border: '1px solid #e0e0e0',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontSize: '12px', 
                    color: '#666', 
                    marginBottom: '4px',
                    textTransform: 'uppercase',
                    fontWeight: '600'
                  }}>
                    Calendar
                  </div>
                  <div style={{ 
                    fontSize: '18px', 
                    fontWeight: '600', 
                    marginBottom: '16px',
                    color: '#1e293b'
                  }}>
                    {contact.calendarId?.title || 'Unknown Calendar'}
                  </div>
                  
                  <div style={{ display: 'grid', gap: '12px' }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                        Contact Person
                      </div>
                      <div style={{ fontWeight: '500' }}>{contact.name}</div>
                    </div>
                    
                    <div>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                        Phone Number
                      </div>
                      <div style={{ fontWeight: '500', fontFamily: 'monospace' }}>
                        {contact.phoneNumber}
                      </div>
                    </div>
                    
                    {contact.email && (
                      <div>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                          Email
                        </div>
                        <div style={{ fontWeight: '500', fontSize: '14px' }}>
                          {contact.email}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    className="action-button edit"
                    onClick={() => editContact(contact)}
                    style={{ padding: '8px 12px' }}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    className="action-button delete"
                    onClick={() => deleteContact(contact.calendarId._id || contact.calendarId)}
                    style={{ padding: '8px 12px' }}
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Edit/Create Modal */}
      <AnimatePresence>
        {editingContact && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setEditingContact(null)}
          >
            <motion.div
              className="modal-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '500px' }}
            >
              <h3>{editingContact.calendarId ? 'Edit' : 'Add'} Admin Contact</h3>
              
              <div className="modal-form">
                <div className="form-group">
                  <label>Calendar *</label>
                  <select
                    value={editingContact.calendarId}
                    onChange={(e) => setEditingContact({ 
                      ...editingContact, 
                      calendarId: e.target.value 
                    })}
                    className="form-input"
                    required
                  >
                    <option value="">Select a calendar</option>
                    {calendars.map((cal) => (
                      <option key={cal.id} value={cal.id}>
                        {cal.title}
                      </option>
                    ))}
                  </select>
                  <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                    Which calendar should this contact receive notifications for?
                  </small>
                </div>

                <div className="form-group">
                  <label>Contact Name *</label>
                  <input
                    type="text"
                    value={editingContact.name}
                    onChange={(e) => setEditingContact({ 
                      ...editingContact, 
                      name: e.target.value 
                    })}
                    placeholder="John Doe"
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    value={editingContact.phoneNumber}
                    onChange={(e) => setEditingContact({ 
                      ...editingContact, 
                      phoneNumber: e.target.value 
                    })}
                    placeholder="+1 (555) 123-4567"
                    className="form-input"
                    required
                  />
                  <small style={{ color: '#666', marginTop: '4px', display: 'block' }}>
                    Include country code (e.g., +1 for US)
                  </small>
                </div>

                <div className="form-group">
                  <label>Email (Optional)</label>
                  <input
                    type="email"
                    value={editingContact.email}
                    onChange={(e) => setEditingContact({ 
                      ...editingContact, 
                      email: e.target.value 
                    })}
                    placeholder="john@example.com"
                    className="form-input"
                  />
                </div>

                <div style={{ 
                  padding: '12px', 
                  background: '#e7f3ff', 
                  borderRadius: '8px',
                  border: '1px solid #b3d9ff',
                  marginTop: '16px'
                }}>
                  <div style={{ fontSize: '14px', color: '#0066cc', marginBottom: '4px', fontWeight: '600' }}>
                    📱 SMS Notifications
                  </div>
                  <div style={{ fontSize: '13px', color: '#333' }}>
                    This contact will receive a text message with a link to assign a setter whenever a new appointment is booked for this calendar.
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button 
                  className="cancel-button" 
                  onClick={() => setEditingContact(null)}
                >
                  Cancel
                </button>
                <button 
                  className="save-button" 
                  onClick={saveContact}
                >
                  Save Contact
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default AdminContactsManagement;